import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { PageHeader, Loading } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { ChannelWizard } from "@/components/ChannelWizard";
import { Plus, Television, X, Repeat, CalendarBlank, Trash, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Channels() {
  const [channels, setChannels] = useState(null);
  const [trashed, setTrashed] = useState(null);
  const [viewers, setViewers] = useState({});
  const [tab, setTab] = useState("active");
  const [showWizard, setShowWizard] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["owner", "admin", "operator"].includes(user?.role);
  const canDelete = ["owner", "admin"].includes(user?.role);

  const load = () => api.get("/channels").then((r) => setChannels(r.data));
  const loadTrash = () => api.get("/channels/trash").then((r) => setTrashed(r.data));
  const loadViewers = () => api.get("/channels/viewers").then((r) => setViewers(r.data.counts || {})).catch(() => {});
  useEffect(() => { load(); loadTrash(); }, []);
  useEffect(() => { loadViewers(); const t = setInterval(loadViewers, 10000); return () => clearInterval(t); }, []);

  const restore = async (c) => {
    if (!window.confirm(
      `Restore "${c.name}"?\n\n• It returns OFF AIR (it will NOT go live automatically)\n• You must publish it again to make it live\n• Restore is subject to your organization's channel entitlement`
    )) return;
    try {
      await api.post(`/channels/${c.id}/restore`);
      toast.success("Channel restored (off air)");
      load(); loadTrash();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const permanentDelete = async (c) => {
    const typed = window.prompt(
      `PERMANENTLY DELETE "${c.name}"?\n\nThis cannot be undone.\n• The channel configuration is removed\n• Its schedules and outputs are removed\n• Asset Library media is NOT deleted\n\nType DELETE to confirm:`
    );
    if (typed !== "DELETE") { if (typed !== null) toast.error("Deletion cancelled — confirmation text did not match."); return; }
    try {
      await api.delete(`/channels/${c.id}`);
      toast.success("Channel permanently deleted");
      loadTrash();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const TabBtn = ({ id, label, count }) => (
    <button data-testid={`channels-tab-${id}`} onClick={() => setTab(id)}
      className={`text-sm px-3 py-1.5 rounded-sm transition-colors duration-150 ${tab === id ? "bg-[#3A86FF] text-white" : "text-[#A3A3A3] hover:text-white"}`}>
      {label}{count != null && count > 0 ? ` (${count})` : ""}
    </button>
  );

  return (
    <>
      <PageHeader title="Channels" subtitle="Linear channel lineup"
        actions={canEdit && (
          <button data-testid="new-channel-btn" onClick={() => setShowWizard(true)}
            className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm">
            <Plus size={16} weight="bold" /> New Channel
          </button>
        )} />

      <div className="px-8 pt-6 flex items-center gap-2" data-testid="channels-tabs">
        <TabBtn id="active" label="Active" count={channels?.length} />
        <TabBtn id="trash" label="Trash" count={trashed?.length} />
      </div>

      <div className="p-8 pt-4 ccs-enter">
        {tab === "trash" ? (
          !trashed ? <Loading /> : trashed.length === 0 ? (
            <div className="text-center py-24 text-[#737373]" data-testid="trash-empty">
              <Trash size={40} className="mx-auto mb-3 opacity-50" />
              <p className="text-sm">Trash is empty. Channels you move to Trash appear here and can be restored.</p>
            </div>
          ) : (
            <div className="border border-[#262626] rounded-sm overflow-hidden" data-testid="trash-list">
              <table className="w-full text-sm">
                <thead className="bg-[#141414] text-[#737373] text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left font-bold px-4 py-3">Channel</th>
                    <th className="text-left font-bold px-4 py-3">Mode</th>
                    <th className="text-left font-bold px-4 py-3">Trashed</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {trashed.map((c) => (
                    <tr key={c.id} data-testid={`trash-row-${c.id}`} className="border-t border-[#262626]">
                      <td className="px-4 py-3">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-[#737373]">{c.description || "No description"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#A3A3A3]">{(c.mode || "scheduled") === "loop" ? "Loop" : "Scheduled"} · {c.resolution}</td>
                      <td className="px-4 py-3 text-xs font-mono text-[#737373]">{c.trashed_at ? new Date(c.trashed_at).toLocaleString() : "—"}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canEdit && (
                          <button data-testid={`restore-channel-${c.id}`} onClick={() => restore(c)}
                            className="inline-flex items-center gap-1.5 text-xs text-emerald-500 hover:bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-3 py-1.5 mr-2 transition-colors duration-150">
                            <ArrowCounterClockwise size={14} /> Restore
                          </button>
                        )}
                        {canDelete && (
                          <button data-testid={`purge-channel-${c.id}`} onClick={() => permanentDelete(c)}
                            className="inline-flex items-center gap-1.5 text-xs text-red-500 hover:bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-1.5 transition-colors duration-150">
                            <Trash size={14} /> Delete Permanently
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          !channels ? <Loading /> : channels.length === 0 ? (
          <div className="text-center py-24 text-[#737373]">
            <Television size={40} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">No channels yet. Create your first channel to begin.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {channels.map((c) => {
              const mode = c.mode || "scheduled";
              const ModeIcon = mode === "loop" ? Repeat : CalendarBlank;
              return (
              <div key={c.id} data-testid={`channel-card-${c.id}`} onClick={() => navigate(`/channels/${c.id}`)}
                className="bg-[#141414] border border-[#262626] rounded-sm hover:border-[#3A86FF] cursor-pointer transition-colors duration-150 group">
                <div className="h-28 bg-[#0A0A0A] border-b border-[#262626] flex items-center justify-center relative overflow-hidden">
                  {c.brand?.logo
                    ? <img src={c.brand.logo} alt="" className="max-h-14 max-w-[60%] object-contain opacity-90" />
                    : <Television size={32} className="text-[#333] group-hover:text-[#3A86FF] transition-colors duration-150" />}
                  <div className="absolute top-3 right-3"><StatusBadge status={c.status} /></div>
                  {c.brand?.accent_color && <div className="absolute left-0 top-0 h-full w-1" style={{ backgroundColor: c.brand.accent_color }} />}
                </div>
                <div className="p-4">
                  <h3 className="font-head font-semibold text-base truncate">{c.name}</h3>
                  <p className="text-xs text-[#737373] mt-1 line-clamp-2 h-8">{c.description || "No description"}</p>
                  <div className="flex items-center gap-2 mt-3 font-mono text-[10px] text-[#525252] uppercase tracking-wider">
                    <span className="flex items-center gap-1 text-[#A3A3A3]"><ModeIcon size={12} /> {mode === "loop" ? "Loop" : "Scheduled"}</span>
                    <span>·</span><span>{c.resolution}</span><span>·</span><span>HLS</span>
                  </div>
                  <div data-testid={`channel-viewers-${c.id}`} className="flex items-center gap-1.5 mt-2 font-mono text-[10px] uppercase tracking-wider">
                    <span className="relative flex h-2 w-2">
                      {viewers[c.id] > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />}
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${viewers[c.id] > 0 ? "bg-emerald-500" : "bg-neutral-600"}`} />
                    </span>
                    <span className={viewers[c.id] > 0 ? "text-emerald-400" : "text-[#525252]"}>Watching Now: {viewers[c.id] || 0}</span>
                  </div>
                </div>
              </div>
            );})}
          </div>
        ))}
      </div>

      {showWizard && <ChannelWizard onClose={() => { setShowWizard(false); load(); }} onCreated={(ch) => { setShowWizard(false); navigate(`/channels/${ch.id}`); }} />}
    </>
  );
}

export function Modal({ title, onClose, children, wide, size }) {
  const width = size === "2xl" ? "max-w-6xl xl:max-w-7xl" : size === "xl" ? "max-w-4xl" : size === "lg" ? "max-w-2xl" : wide ? "max-w-lg" : "max-w-md";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div onClick={(e) => e.stopPropagation()}
        className={`bg-[#141414] border border-[#262626] rounded-sm w-full ccs-enter ${width}`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
          <h3 className="font-head font-semibold text-sm">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-[#737373] hover:text-white transition-colors duration-150"><X size={18} /></button>
        </div>
        <div className="p-5 max-h-[80vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export function ModalField({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">{label}</label>
      <input data-testid={testid} {...props}
        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none transition-colors duration-150" />
    </div>
  );
}

export function ModalTextarea({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">{label}</label>
      <textarea data-testid={testid} {...props}
        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none transition-colors duration-150 resize-y min-h-[72px]" />
    </div>
  );
}

export function ModalSelect({ label, testid, children, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">{label}</label>
      <select data-testid={testid} {...props}
        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none transition-colors duration-150">
        {children}
      </select>
    </div>
  );
}
